import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Plus, Search, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";
import { formatDate, formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  useIncidents,
  useUpdateIncidentInList,
  type IncidentListItem,
  type IncidentFilters,
  type IncidentSortField,
  type IncidentStatus,
} from "./api";
import {
  ariaSort,
  discoveredRange,
  nextSort,
  type IncidentSort,
} from "./list-sort";
import { useMasterData } from "../master-data/api";
import { toUserMap, useUsers, userName } from "../users/api";
import type { IncidentListSearch } from "./search";
import {
  ALL,
  rememberListSearch,
  searchFromView,
  viewFromSearch,
  type DeepFilters,
  type IncidentListView,
} from "./list-view";
import { STATUS_LABEL_KEY, STATUS_ORDER } from "./status";
import { OPEN_STATUSES } from "../dashboard/drilldown";
import { sameSelection, seedStatuses, statusesParam, toggleStatus } from "./status-filter";
import { StatusBadge } from "./components/status-badge";
import { ArchivedBadge } from "./components/archived-badge";
import { SeverityLabel } from "./components/severity-label";
import { canAssign, canCreate, canEdit, canReassignRetiredOwner } from "./permissions";
import { isIncidentParty } from "../../lib/access";
import { activeUsers } from "../users/api";
import { notifyApiError } from "../../lib/toast-error";
import { downloadIncidentsCsv } from "../export/api";
import { ExportButton } from "../export/export-button";
import { canExport } from "../export/permissions";

/** Debounce of the toolbar search (ms) - mirrors the M-006/M-008 searches. */
const SEARCH_DEBOUNCE_MS = 300;

/** The Select value for "no Owner" (Radix Select has no empty value). */
const NO_OWNER = "__none__";

/** The F-011 sort columns in table order, with their header label keys. */
const SORT_COLUMNS: ReadonlyArray<{ field: IncidentSortField; labelKey: string }> = [
  { field: "number", labelKey: "list.colNumber" },
  { field: "title", labelKey: "list.colTitle" },
  { field: "status", labelKey: "list.colStatus" },
  { field: "severity", labelKey: "list.colSeverity" },
  { field: "discoveredAt", labelKey: "list.colDiscovered" },
  { field: "createdAt", labelKey: "list.colCreated" },
];

/** The "Offen" quick chip is a preset of the Status selection (CD-012). */
const OPEN_SELECTION = seedStatuses({ statuses: OPEN_STATUSES });

type Update<T> = T | ((current: T) => T);

export function IncidentListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  /* F-011: the list's view - filters, search term, sort - IS the URL's search
   * (list-view.ts). Every control writes the URL (replacing the entry, so
   * typing does not fill the history), and the list reads it back, so the
   * back button, a reload and a shared link show the same list. */
  const search = useSearch({ strict: false }) as IncidentListSearch;
  const view = viewFromSearch(search);
  const {
    statusSel,
    severityFilter,
    areaFilter,
    tagFilter,
    ownerFilter,
    discoveredFromDay,
    discoveredToDay,
    mineOnly,
    withArchive,
    deepFilters,
    sort,
  } = view;

  const writeView = (next: IncidentListView): void => {
    void navigate({ to: "/incidents", search: searchFromView(next), replace: true });
  };
  const setter =
    <K extends keyof IncidentListView>(key: K) =>
    (value: Update<IncidentListView[K]>): void => {
      const resolved =
        typeof value === "function"
          ? (value as (current: IncidentListView[K]) => IncidentListView[K])(view[key])
          : value;
      writeView({ ...view, [key]: resolved });
    };
  const setStatusSel = setter("statusSel");
  const setSeverityFilter = setter("severityFilter");
  const setAreaFilter = setter("areaFilter");
  const setTagFilter = setter("tagFilter");
  const setOwnerFilter = setter("ownerFilter");
  const setDiscoveredFromDay = setter("discoveredFromDay");
  const setDiscoveredToDay = setter("discoveredToDay");
  const setMineOnly = setter("mineOnly");
  const setWithArchive = setter("withArchive");
  const setDeepFilters = setter("deepFilters");
  const setSort = setter("sort");

  // The detail page's "Zurück zur Liste" returns to this very list.
  const canonical = JSON.stringify(searchFromView(view));
  useEffect(() => {
    rememberListSearch(JSON.parse(canonical) as IncidentListSearch);
  }, [canonical]);

  // M-002 toolbar: debounced free-text search plus the "Meine" quick filter.
  // The field is local while typing; the debounced term goes to the URL, and
  // a term arriving from the URL (back button, link) fills the field.
  const query = view.q;
  const [searchInput, setSearchInput] = useState(query);
  const writtenQuery = useRef(query);
  const latestView = useRef(view);
  latestView.current = view;
  useEffect(() => {
    if (query !== writtenQuery.current) {
      writtenQuery.current = query;
      setSearchInput(query);
    }
  }, [query]);
  useEffect(() => {
    const term = searchInput.trim();
    if (term === writtenQuery.current) return undefined;
    const handle = setTimeout(() => {
      writtenQuery.current = term;
      writeView({ ...latestView.current, q: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // The view is read when the timer fires (latestView); only typing restarts it.
  }, [searchInput]);

  const clearDeepFilter = (key: keyof DeepFilters): void => {
    setDeepFilters((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  /* The Offen chip is a preset of the Status selection and the Überfällig
   * chip writes the overdueFollowup slot the dashboard drilldown seeds, so a
   * drilldown lights the chip instead of fighting it. */
  const openChipActive = sameSelection(statusSel, OPEN_SELECTION);
  const overdueChipActive = deepFilters.overdueFollowup === true;

  const toggleOpenChip = (): void => {
    setStatusSel(openChipActive ? [] : OPEN_SELECTION);
  };
  const toggleOverdueChip = (): void => {
    if (overdueChipActive) clearDeepFilter("overdueFollowup");
    else setDeepFilters((current) => ({ ...current, overdueFollowup: true }));
  };

  const resetFilters = (): void => {
    setSearchInput("");
    writtenQuery.current = "";
    // The sort is not a filter and survives the reset.
    writeView({ ...viewFromSearch({}), sort });
  };

  const discovered = discoveredRange(discoveredFromDay, discoveredToDay);
  const filters: IncidentFilters = {
    ...(query !== "" ? { q: query } : {}),
    ...(statusesParam(statusSel) ? { statuses: statusesParam(statusSel) } : {}),
    ...(severityFilter !== ALL ? { severityId: severityFilter } : {}),
    ...(areaFilter !== ALL ? { areaId: areaFilter } : {}),
    ...(tagFilter !== ALL ? { tagId: tagFilter } : {}),
    ...(ownerFilter !== ALL ? { ownerId: ownerFilter } : {}),
    ...discovered,
    ...(mineOnly ? { mine: true } : {}),
    ...(withArchive ? { archived: "include" as const } : {}),
    ...deepFilters,
    sort: sort.field,
    dir: sort.dir,
  };

  const incidents = useIncidents(filters);
  const { severities, categories, areas, tags, severityById, categoryById } = useMasterData();
  const usersQuery = useUsers();
  const userById = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);

  const items = incidents.data?.pages.flatMap((page) => page.items) ?? [];
  // `total` counts the full filtered set; the LAST page carries the freshest.
  const lastPage = incidents.data?.pages[incidents.data.pages.length - 1];
  const isFiltered =
    statusSel.length > 0 ||
    severityFilter !== ALL ||
    areaFilter !== ALL ||
    tagFilter !== ALL ||
    ownerFilter !== ALL ||
    Object.keys(discovered).length > 0 ||
    Object.keys(deepFilters).length > 0 ||
    mineOnly ||
    withArchive ||
    query !== "";
  const showReset = isFiltered || searchInput.trim() !== "";
  const showCreate = canCreate(user);
  const showExport = canExport(user);
  // CD-006: each inline editor asks the row's own `can`, the same answer the
  // detail view gets - severity and category are `incident.edit`, the Owner
  // `incident.assign` (or, once the Owner is deactivated, the reassignment).
  const ownerRetired = (item: IncidentListItem): boolean =>
    item.ownerId !== null && !userById.get(item.ownerId)?.active;
  const ownerOptions = (item: IncidentListItem) => [
    { value: NO_OWNER, label: t("list.unassigned") },
    ...activeUsers(usersQuery.data)
      .filter((u) => u.id !== item.ownerId)
      .map((u) =>
        isIncidentParty(u)
          ? { value: u.id, label: userName(u.id, userById) }
          : {
              value: u.id,
              label: t("incidentParty.notEligible", { name: userName(u.id, userById) }),
              disabled: true,
            },
      ),
    ...(item.ownerId
      ? [{ value: item.ownerId, label: userName(item.ownerId, userById) }]
      : []),
  ];

  // Removable chips: only deep filters WITHOUT a toolbar control of their own.
  const hasDeepChips =
    Boolean(deepFilters.categoryId) ||
    Boolean(deepFilters.createdAfter) ||
    Boolean(deepFilters.missingPostmortem);


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("list.title")}
          </h1>
          {lastPage ? (
            <p data-testid="incidents_count" className="text-sm text-muted-foreground">
              {t("list.countLine", {
                shown: items.length,
                total: lastPage.total,
              })}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* CSV export of the CURRENT filter state (F-012, US-011). */}
          {showExport ? (
            <ExportButton
              testid="incidents_export-csv"
              label={t("export.csv")}
              successMessage={t("export.csvDone")}
              download={() => downloadIncidentsCsv(filters)}
            />
          ) : null}
          {showCreate ? (
            <Button asChild className="h-11 sm:h-9">
              <Link to="/incidents/new" data-testid="incidents_create">
                <Plus />
                {t("nav.newIncident")}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {/* The filter row (F-011): the search and every filter on one band, so
       * the controls that do one job read as one thing (CD-014 - a reviewer
       * did not recognise the search while it sat apart from the Selects). */}
      <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
        <div className="col-span-2 space-y-1.5 sm:min-w-64 sm:flex-1">
          <label htmlFor="incidents-search" className="text-sm font-medium">
            {t("list.searchLabel")}
          </label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="incidents-search"
              data-testid="incidents_search"
              className="pl-9"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("list.searchPlaceholder")}
            />
          </div>
        </div>

        <StatusMultiSelect selected={statusSel} onToggle={(status) => setStatusSel((current) => toggleStatus(current, status))} />
        <FilterSelect
          testid="incidents_filter-severity"
          label={t("list.filterSeverity")}
          value={severityFilter}
          onChange={setSeverityFilter}
          options={(severities.data ?? []).map((severity) => ({
            value: severity.id,
            label: severity.name,
          }))}
        />
        <FilterSelect
          testid="incidents_filter-area"
          label={t("list.filterArea")}
          value={areaFilter}
          onChange={setAreaFilter}
          options={(areas.data ?? []).map((area) => ({ value: area.id, label: area.name }))}
        />
        <FilterSelect
          testid="incidents_filter-tag"
          label={t("list.filterTag")}
          value={tagFilter}
          onChange={setTagFilter}
          options={(tags.data ?? []).map((tag) => ({ value: tag.id, label: tag.name }))}
        />
        <FilterSelect
          testid="incidents_filter-owner"
          label={t("list.filterOwner")}
          value={ownerFilter}
          onChange={setOwnerFilter}
          options={(usersQuery.data ?? []).map((user) => ({
            value: user.id,
            label: userName(user.id, userById),
          }))}
        />
        <div className="space-y-1.5">
          <label htmlFor="incidents-discovered-from" className="text-sm font-medium">
            {t("list.filterDiscoveredFrom")}
          </label>
          <Input
            id="incidents-discovered-from"
            data-testid="incidents_filter-discovered-from"
            type="date"
            className="w-full sm:w-40"
            value={discoveredFromDay}
            max={discoveredToDay || undefined}
            onChange={(event) => setDiscoveredFromDay(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="incidents-discovered-to" className="text-sm font-medium">
            {t("list.filterDiscoveredTo")}
          </label>
          <Input
            id="incidents-discovered-to"
            data-testid="incidents_filter-discovered-to"
            type="date"
            className="w-full sm:w-40"
            value={discoveredToDay}
            min={discoveredFromDay || undefined}
            onChange={(event) => setDiscoveredToDay(event.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {t("list.quickFilters")}
        </span>
        <QuickFilterChip
          testid="incidents_quick-open"
          label={t("list.quickOpen")}
          active={openChipActive}
          onToggle={toggleOpenChip}
        />
        <QuickFilterChip
          testid="incidents_quick-overdue"
          label={t("list.quickOverdue")}
          active={overdueChipActive}
          onToggle={toggleOverdueChip}
        />
        <QuickFilterChip
          testid="incidents_quick-mine"
          label={t("list.quickMine")}
          active={mineOnly}
          onToggle={() => setMineOnly((current) => !current)}
        />
        <QuickFilterChip
          testid="incidents_quick-archived"
          label={t("list.quickArchived")}
          active={withArchive}
          onToggle={() => setWithArchive((current) => !current)}
        />
        {showReset ? (
          <Button
            data-testid="incidents_reset"
            variant="ghost"
            size="sm"
            className="min-h-11 sm:min-h-8"
            onClick={resetFilters}
          >
            {t("list.reset")}
          </Button>
        ) : null}
      </div>

      {/* Deep-linked filters without a toolbar control (dashboard drilldowns):
       * a visible, removable chip row so the filter is never invisible. */}
      {hasDeepChips ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {t("list.activeFilters")}
          </span>
          {deepFilters.categoryId ? (
            <FilterChip
              testid="incidents_chip-category"
              label={t("list.chipCategory", {
                value:
                  categoryById.get(deepFilters.categoryId)?.name ??
                  deepFilters.categoryId,
              })}
              onRemove={() => clearDeepFilter("categoryId")}
            />
          ) : null}
          {deepFilters.createdAfter ? (
            <FilterChip
              testid="incidents_chip-created-after"
              label={t("list.chipCreatedAfter", {
                value: formatDate(deepFilters.createdAfter),
              })}
              onRemove={() => clearDeepFilter("createdAfter")}
            />
          ) : null}
          {deepFilters.missingPostmortem ? (
            <FilterChip
              testid="incidents_chip-missing-postmortem"
              label={t("list.chipMissingPostmortem")}
              onRemove={() => clearDeepFilter("missingPostmortem")}
            />
          ) : null}
        </div>
      ) : null}

      {incidents.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{t("errors.load")}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void incidents.refetch()}
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : incidents.isLoading ? (
        <IncidentTableSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed px-6 py-16 text-center">
          <p className="text-muted-foreground">
            {isFiltered ? t("list.emptyFiltered") : t("list.empty")}
          </p>
          {isFiltered ? (
            <Button
              data-testid="incidents_empty-reset"
              variant="outline"
              className="mt-4"
              onClick={resetFilters}
            >
              {t("list.resetFilters")}
            </Button>
          ) : showCreate ? (
            <Button asChild className="mt-4">
              <Link to="/incidents/new" data-testid="incidents_empty-create">
                <Plus />
                {t("list.createFirst")}
              </Link>
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          {/* Desktop: the full table. */}
          <div className="hidden rounded-lg border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead
                    testid="incidents_sort-number"
                    field="number"
                    label={t("list.colNumber")}
                    sort={sort}
                    onSort={setSort}
                    className="w-40"
                  />
                  <SortableHead
                    testid="incidents_sort-title"
                    field="title"
                    label={t("list.colTitle")}
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableHead
                    testid="incidents_sort-status"
                    field="status"
                    label={t("list.colStatus")}
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableHead
                    testid="incidents_sort-severity"
                    field="severity"
                    label={t("list.colSeverity")}
                    sort={sort}
                    onSort={setSort}
                  />
                  <TableHead>{t("list.colCategory")}</TableHead>
                  <TableHead>{t("list.colOwner")}</TableHead>
                  <SortableHead
                    testid="incidents_sort-discovered"
                    field="discoveredAt"
                    label={t("list.colDiscovered")}
                    sort={sort}
                    onSort={setSort}
                  />
                  <SortableHead
                    testid="incidents_sort-created"
                    field="createdAt"
                    label={t("list.colCreated")}
                    sort={sort}
                    onSort={setSort}
                    className="hidden 2xl:table-cell"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    data-testid={`incidents_row_${item.number}`}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: "/incidents/$id",
                        params: { id: item.id },
                      })
                    }
                  >
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      <Link
                        to="/incidents/$id"
                        params={{ id: item.id }}
                        data-testid={`incidents_row-open_${item.number}`}
                        className="text-primary-text underline-offset-4 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.number}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell>
                      <StatusBadge status={item.status} />
                      {item.archivedAt ? (
                        <span className="ml-1.5 inline-block">
                          <ArchivedBadge
                            testid={`incidents_row-archived_${item.number}`}
                            archivedAt={item.archivedAt}
                          />
                        </span>
                      ) : null}
                    </TableCell>
                    <>
                      {canEdit(item) ? (
                        <TableCell>
                          <InlineSelect
                            testid={`incidents_edit-severity_${item.number}`}
                            label={t("list.colSeverity")}
                            item={item}
                            field="severityId"
                            value={item.severityId}
                            options={(severities.data ?? [])
                              .filter((s) => s.active || s.id === item.severityId)
                              .map((s) => ({ value: s.id, label: s.name }))}
                          />
                        </TableCell>
                      ) : (
                        <TableCell>
                          <SeverityLabel severity={severityById.get(item.severityId)} />
                        </TableCell>
                      )}
                      {canEdit(item) ? (
                        <TableCell>
                          <InlineSelect
                            testid={`incidents_edit-category_${item.number}`}
                            label={t("list.colCategory")}
                            item={item}
                            field="categoryId"
                            value={item.categoryId}
                            options={(categories.data ?? [])
                              .filter((c) => c.active || c.id === item.categoryId)
                              .map((c) => ({ value: c.id, label: c.name }))}
                          />
                        </TableCell>
                      ) : (
                        <TableCell className="text-muted-foreground">
                          {categoryById.get(item.categoryId)?.name ??
                            t("common.none")}
                        </TableCell>
                      )}
                      {canAssign(item) || canReassignRetiredOwner(item, ownerRetired(item)) ? (
                        <TableCell>
                          <InlineSelect
                            testid={`incidents_edit-owner_${item.number}`}
                            label={t("list.colOwner")}
                            item={item}
                            field="ownerId"
                            value={item.ownerId ?? NO_OWNER}
                            options={ownerOptions(item)}
                          />
                        </TableCell>
                      ) : (
                        <TableCell className="text-muted-foreground">
                          {item.ownerId
                            ? userName(item.ownerId, userById)
                            : t("list.unassigned")}
                        </TableCell>
                      )}
                    </>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDateTime(item.discoveredAt)}
                    </TableCell>
                    {/* 2xl: the inline editors (CD-006) take the width below. */}
                    <TableCell className="hidden whitespace-nowrap text-muted-foreground 2xl:table-cell">
                      {formatDateTime(item.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile (<md): card list instead of the table (M-002). The cards
           * have no column headers, so the sort gets a Select of its own. */}
          <div className="md:hidden">
            <MobileSortSelect sort={sort} onSort={setSort} />
          </div>
          <ul className="space-y-3 md:hidden">
            {items.map((item) => (
              <li key={item.id}>
                <Link
                  to="/incidents/$id"
                  params={{ id: item.id }}
                  data-testid={`incidents_card_${item.number}`}
                  className="block space-y-2 rounded-lg border p-3"
                >
                  <p className="font-mono text-xs text-muted-foreground">
                    {item.number}
                  </p>
                  <p className="font-medium">{item.title}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={item.status} />
                    {item.archivedAt ? (
                      <ArchivedBadge
                        testid={`incidents_card-archived_${item.number}`}
                        archivedAt={item.archivedAt}
                      />
                    ) : null}
                    <SeverityLabel severity={severityById.get(item.severityId)} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {item.ownerId
                      ? userName(item.ownerId, userById)
                      : t("list.unassigned")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {categoryById.get(item.categoryId)?.name ?? t("common.none")}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {incidents.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            data-testid="incidents_load-more"
            variant="outline"
            onClick={() => void incidents.fetchNextPage()}
            disabled={incidents.isFetchingNextPage}
          >
            {incidents.isFetchingNextPage
              ? t("common.loading")
              : t("common.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** One Select of the filter row: "Alle" plus the given options (F-011). */
function FilterSelect({
  testid,
  label,
  value,
  onChange,
  options,
}: {
  testid: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="w-full sm:w-44" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("list.filterAll")}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * A list cell edited in place (CD-006 / LP-027): Schweregrad, Kategorie or
 * Owner through the same PATCH, If-Match and audit as the detail view. A
 * click on it does not open the row.
 */
function InlineSelect({
  testid,
  label,
  item,
  field,
  value,
  options,
}: {
  testid: string;
  label: string;
  item: IncidentListItem;
  field: "severityId" | "categoryId" | "ownerId";
  value: string;
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
}): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateIncidentInList();
  const onChange = (next: string): void => {
    if (next === value) return;
    update.mutate(
      {
        id: item.id,
        version: item.version,
        field,
        value: field === "ownerId" && next === NO_OWNER ? null : next,
      },
      {
        onSuccess: () => toast.success(t("list.inlineSaved", { number: item.number })),
        onError: (error) =>
          notifyApiError(error, {
            precondition: t("errors.precondition"),
            forbidden: t("errors.forbidden"),
            conflict: t("errors.conflict"),
            generic: t("errors.saveFailed"),
          }),
      },
    );
  };
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Select value={value} onValueChange={onChange} disabled={update.isPending}>
        <SelectTrigger
          data-testid={testid}
          aria-label={`${label}: ${item.number}`}
          className="h-8 border-transparent bg-transparent px-2 shadow-none hover:border-input"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled === true}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * The Status filter as a multi-select (CD-012): one trigger, a checkbox per
 * status, and the menu stays open while ticking several.
 */
function StatusMultiSelect({
  selected,
  onToggle,
}: {
  selected: readonly IncidentStatus[];
  onToggle: (status: IncidentStatus) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const summary =
    selected.length === 0
      ? t("list.filterAll")
      : selected.length === 1
        ? t(STATUS_LABEL_KEY[selected[0] as IncidentStatus])
        : t("list.filterStatusCount", { count: selected.length });
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{t("list.filterStatus")}</label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="incidents_filter-status"
            variant="outline"
            aria-label={t("list.filterStatus")}
            className="flex h-10 w-full justify-between px-3 font-normal sm:w-44"
          >
            <span className="truncate">{summary}</span>
            <ChevronDown aria-hidden="true" className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {STATUS_ORDER.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              data-testid={`incidents_filter-status-option_${status}`}
              checked={selected.includes(status)}
              onCheckedChange={() => onToggle(status)}
              onSelect={(event) => event.preventDefault()}
            >
              {t(STATUS_LABEL_KEY[status])}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** A table header that sorts by its column (F-011, "Sortierung je Spalte"). */
function SortableHead({
  testid,
  field,
  label,
  sort,
  onSort,
  className,
}: {
  testid: string;
  field: IncidentSortField;
  label: string;
  sort: IncidentSort;
  onSort: (next: IncidentSort) => void;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const state = ariaSort(sort, field);
  const Icon = state === "ascending" ? ArrowUp : state === "descending" ? ArrowDown : ArrowUpDown;
  return (
    <TableHead aria-sort={state} className={className}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onSort(nextSort(sort, field))}
        aria-label={t("list.sortBy", { column: label })}
        className="-ml-2 inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-muted hover:text-foreground"
      >
        {label}
        <Icon
          aria-hidden="true"
          className={cn("h-3.5 w-3.5", state === "none" && "text-muted-foreground/50")}
        />
      </button>
    </TableHead>
  );
}

/** The sort as one Select for the card layout, which has no headers. */
function MobileSortSelect({
  sort,
  onSort,
}: {
  sort: IncidentSort;
  onSort: (next: IncidentSort) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const value = `${sort.field}:${sort.dir}`;
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{t("list.sortLabel")}</label>
      <Select
        value={value}
        onValueChange={(next) => {
          const [field, dir] = next.split(":");
          onSort({ field: field as IncidentSortField, dir: dir === "asc" ? "asc" : "desc" });
        }}
      >
        <SelectTrigger data-testid="incidents_sort-mobile" className="w-full" aria-label={t("list.sortLabel")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_COLUMNS.flatMap((column) =>
            (["asc", "desc"] as const).map((dir) => (
              <SelectItem key={`${column.field}:${dir}`} value={`${column.field}:${dir}`}>
                {t(dir === "asc" ? "list.sortOptionAsc" : "list.sortOptionDesc", {
                  column: t(column.labelKey),
                })}
              </SelectItem>
            )),
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Toggle chip of the Schnellfilter row (M-002); 44px touch target on mobile. */
function QuickFilterChip({
  testid,
  label,
  active,
  onToggle,
}: {
  testid: string;
  label: string;
  active: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <Button
      data-testid={testid}
      type="button"
      variant={active ? "default" : "outline"}
      size="sm"
      aria-pressed={active}
      className="min-h-11 rounded-full sm:min-h-8"
      onClick={onToggle}
    >
      {label}
    </Button>
  );
}

function FilterChip({
  testid,
  label,
  onRemove,
}: {
  testid: string;
  label: string;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-muted py-0.5 pl-2.5 pr-1">
      {label}
      <button
        data-testid={testid}
        type="button"
        aria-label={`${t("list.chipRemove")}: ${label}`}
        onClick={onRemove}
        className="rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
      >
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

/** Table-shaped skeleton (K6): a header row plus 7 rows with per-column
 *  width variety, so the loading state pre-figures the real table layout. */
function IncidentTableSkeleton(): JSX.Element {
  // Column widths mirror the table columns (number, title, status, severity,
  // category, owner, discovered, created).
  const columns = ["w-20", "w-full", "w-16", "w-24", "w-24", "w-28", "w-24", "w-24"];
  return (
    <div className="rounded-lg border">
      <div className="flex gap-4 border-b px-4 py-3">
        {columns.map((width, index) => (
          <div key={index} className={cn("flex-1", width === "w-full" && "flex-[2]")}>
            <div className={cn("h-3 animate-pulse rounded bg-muted", width)} />
          </div>
        ))}
      </div>
      {Array.from({ length: 7 }).map((_, row) => (
        <div key={row} className="flex gap-4 border-b px-4 py-3.5 last:border-b-0">
          {columns.map((width, index) => (
            <div
              key={index}
              className={cn("flex-1", width === "w-full" && "flex-[2]")}
            >
              <div className={cn("h-4 animate-pulse rounded bg-muted", width)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
